import { useState, useEffect } from "react";
import { onboardingSteps } from "../data/onboardingSteps";
import { onBoardingSurvey } from "../../../Controllers/OnboardingApi";
import { useNavigate } from "react-router-dom";
import { useMixpanel } from "../../../Context/MixpanelContext/MixpanelProvider";

// Define the type for structured onboarding responses
export interface OnboardingResponses {
  goal?: string;
  frustration?: string;
  confidence_level?: string;
  league_type?: string;
  league_count_category?: string;
  // Dynamic keys for "other" text inputs
  goal_other_text?: string;
  frustration_other_text?: string;
  confidence_level_other_text?: string;
  league_type_other_text?: string;
  league_count_category_other_text?: string;
  // Allow any additional dynamic keys for future extensibility
  [key: string]: string | undefined;
}

export const useOnboardingLogic = () => {
  // Start at step 0 (normal flow). To test textarea, change to 1 for step 2 or 2 for step 3
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState<"forward" | "backward">("forward");
  const navigate = useNavigate();
  const Mixpanel = useMixpanel();
  const [stepSelections, setStepSelections] = useState<{
    [stepId: number]: number[];
  }>({});
  const [textAreaInputs, setTextAreaInputs] = useState<{
    [stepId: number]: string;
  }>({});
  const [loadingBoardingSurvey, setLoadingBoardingSurvey] = useState(false);
  // New structured state to store responses by field name
  const [onboardingResponses, setOnboardingResponses] =
    useState<OnboardingResponses>({});

  // Helper function to update structured responses
  const updateStructuredResponses = (
    stepId: number,
    selectedIndices: number[],
  ) => {
    const step = onboardingSteps.find((s) => s.id === stepId);
    if (!step) return;

    const selectedValues: string[] = [];
    let hasOtherSelection = false;
    let otherText = "";

    selectedIndices.forEach((index) => {
      const option = step.options[index];
      if (!option) return;

      // If it's "Other" option, handle separately
      if (option.text.toLowerCase() === "other") {
        hasOtherSelection = true;
        const textAreaInput = textAreaInputs[stepId] || "";
        otherText = textAreaInput.trim();
        // Add "Other" to selected values if there's custom text
        if (otherText) {
          selectedValues.push(otherText);
        }
      } else {
        // Use the value field instead of text field
        selectedValues.push(option.value);
      }
    });

    // For single selection steps, use the first (and only) selection
    // For multiple selection steps, join with commas or use array
    const responseValue = step.allowMultipleSelection
      ? selectedValues.join(", ")
      : selectedValues[0] || "";

    setOnboardingResponses((prev) => {
      const updatedResponses = {
        ...prev,
        [step.field]: responseValue,
      };

      // Store "Other" text separately with ${field}_other_text key
      if (hasOtherSelection && otherText) {
        updatedResponses[`${step.field}_other_text`] = otherText;
      } else {
        // Remove the other_text field if "Other" is not selected or has no text
        delete updatedResponses[`${step.field}_other_text`];
      }

      return updatedResponses;
    });
  };

  // Update structured responses when existing selections change
  useEffect(() => {
    // Update all existing selections to structured responses
    onboardingSteps.forEach((step) => {
      const selections = stepSelections[step.id];
      if (selections && selections.length > 0) {
        updateStructuredResponses(step.id, selections);
      }
    });
  }, [stepSelections, textAreaInputs]);

  const getCurrentStepSelections = (): number[] => {
    const current = onboardingSteps[currentStep];
    return stepSelections[current.id] || [];
  };

  const getCurrentTextAreaInput = (): string => {
    const current = onboardingSteps[currentStep];
    return textAreaInputs[current.id] || "";
  };

  const handleTextAreaChange = (value: string) => {
    const current = onboardingSteps[currentStep];
    setTextAreaInputs((prev) => ({
      ...prev,
      [current.id]: value,
    }));

    // Update structured responses for "Other" text input
    if (isOtherOptionSelected()) {
      const trimmedValue = value.trim();
      setOnboardingResponses((prev) => ({
        ...prev,
        [current.field]: trimmedValue,
        [`${current.field}_other_text`]: trimmedValue,
      }));
    }
  };

  // Check if "Other" option is selected and requires textarea
  const isOtherOptionSelected = (): boolean => {
    const current = onboardingSteps[currentStep];
    const currentSelections = getCurrentStepSelections();

    // Check if any selected option has text "Other"
    return currentSelections.some((index) => {
      const option = current.options[index];
      return option && option.text.toLowerCase() === "other";
    });
  };

  const handleOptionSelect = (index: number) => {
    const current = onboardingSteps[currentStep];
    const currentSelections = getCurrentStepSelections();
    const selectedOption = current.options[index];

    let newSelections: number[];
    let isToggleOn = false;

    if (current.allowMultipleSelection) {
      // Multiple selection logic
      if (currentSelections.includes(index)) {
        // Remove if already selected
        newSelections = currentSelections.filter((i) => i !== index);
        isToggleOn = false;
      } else {
        // Add if not selected
        newSelections = [...currentSelections, index];
        isToggleOn = true;
      }
    } else {
      // Single selection logic
      if (currentSelections.includes(index)) {
        // Unselect if already selected
        newSelections = [];
        isToggleOn = false;
      } else {
        // For single selection, deselect previously selected options first
        const previouslySelected = currentSelections[0];
        if (previouslySelected !== undefined) {
          const previousOption = current.options[previouslySelected];
          if (previousOption?.event) {
            Mixpanel.trackEvent(`${previousOption.event}_toggle_off`, {
              value: previousOption.value,
            });
          }
        }

        // Select this option only
        newSelections = [index];
        isToggleOn = true;
      }
    }

    // Track the toggle event for the current option
    if (selectedOption?.event) {
      const eventName = isToggleOn
        ? `${selectedOption.event}_toggle_on`
        : `${selectedOption.event}_toggle_off`;

      Mixpanel.trackEvent(eventName, {
        value: selectedOption.value,
      });
    }

    setStepSelections((prev) => ({
      ...prev,
      [current.id]: newSelections,
    }));

    // Clear textarea input if "Other" option is deselected
    const option = current.options[index];
    if (option && option.text.toLowerCase() === "other") {
      const willBeSelected = !currentSelections.includes(index);
      if (!willBeSelected) {
        setTextAreaInputs((prev) => ({
          ...prev,
          [current.id]: "",
        }));
        // Also clear from structured responses
        setOnboardingResponses((prev) => {
          const updatedResponses = { ...prev };
          updatedResponses[current.field] = "";
          delete updatedResponses[`${current.field}_other_text`];
          return updatedResponses;
        });
      }
    }

    // Update structured responses with new selections
    updateStructuredResponses(current.id, newSelections);
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setDirection("backward");
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleContinue = async () => {
    const currentSelections = getCurrentStepSelections();
    if (currentSelections.length === 0) {
      return; // Don't continue if no selection
    }

    // If "Other" is selected, check if textarea has content
    if (isOtherOptionSelected()) {
      const textAreaInput = getCurrentTextAreaInput().trim();
      if (!textAreaInput) {
        return; // Don't continue if "Other" is selected but no text is provided
      }
    }

    // Track the continue button click event for the current step
    const currentStepData = onboardingSteps[currentStep];
    if (currentStepData.event) {
      Mixpanel.trackEvent(currentStepData.event, {
        step: currentStepData.id,
        stepTitle: currentStepData.title,
      });
    }

    if (currentStep < onboardingSteps.length - 1) {
      setDirection("forward");
      setCurrentStep((prev) => prev + 1);
    } else {
      try {
        // Check if frustration_other_text is not available and set it to null
        setLoadingBoardingSurvey(true);
        const finalOnboardingResponses = {
          ...onboardingResponses,
          frustration_other_text:
            onboardingResponses?.frustration_other_text || null,
        };

        const statusResponse = await onBoardingSurvey(finalOnboardingResponses);
        console.log(statusResponse, "statusResponse");
        // Navigate with checkOnboarding: true only if API is successful
        if (statusResponse.status === 201 && statusResponse.data) {
          navigate("/chatbot-ai", {
            state: {
              showAnimation: true,
              showPaywall: true,
              checkOnboarding: true,
            },
          });
        } else {
          console.log(
            "Error onboarding survey:",
            String(statusResponse.errors),
          );
          // Navigate without checkOnboarding when API fails
          navigate("/chatbot-ai", {
            state: {
              showAnimation: true,
              showPaywall: true,
            },
          });
        }
      } catch (error: any) {
        console.error("Error onboarding survey:", error);
        // Navigate without checkOnboarding when API call throws error
        navigate("/chatbot-ai", {
          state: {
            showAnimation: true,
            showPaywall: true,
          },
        });
      } finally {
        setLoadingBoardingSurvey(false);
      }
      // Handle completion of onboarding
      console.log("Onboarding completed with selections:", stepSelections);
      console.log("Text area inputs:", textAreaInputs);
      console.log("Structured onboarding responses:", onboardingResponses);
      // Navigate to next screen or handle completion
      // This could be moved to a callback prop for better separat ion of concerns
    }
  };

  // Update canContinue logic to include textarea validation
  const canContinue = (() => {
    const currentSelections = getCurrentStepSelections();
    if (currentSelections.length === 0) {
      return false;
    }

    // If "Other" is selected, check if textarea has content
    if (isOtherOptionSelected()) {
      const textAreaInput = getCurrentTextAreaInput().trim();
      return textAreaInput.length > 0;
    }

    return true;
  })();

  const currentStepData = onboardingSteps[currentStep];

  return {
    currentStep,
    currentStepData,
    direction,
    stepSelections,
    textAreaInputs,
    getCurrentStepSelections,
    getCurrentTextAreaInput,
    handleTextAreaChange,
    isOtherOptionSelected,
    handleOptionSelect,
    handleBack,
    handleContinue,
    canContinue,
    isLastStep: currentStep === onboardingSteps.length - 1,
    totalSteps: onboardingSteps.length,
    onboardingResponses,
    loadingBoardingSurvey,
  };
};
